import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { Button } from "react-bootstrap";
import API_URLS from "../../../../constants/apiConstants";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { GeneralDropdown } from "../../../../components/components/js/GeneralDropdown";
import { LoadingDots } from "../../../../components/general";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../../../../hooks/useAuth";

export const StaffIndividualAttendance = ({ reportRef }) => {
  const { t } = useTranslation();
  const { auth, school } = useAuth();
  const trans_prefix = "admin_pages.attendance_reports";
  const trans = {
    title: t(`${trans_prefix}.card_titles.staff_individual`),
    noRecords: t(`${trans_prefix}.card_titles.no_records`),
    selectStaff: t(`${trans_prefix}.filters.select_staff`),
    monthYear: t(`${trans_prefix}.filters.month_year`),
    requiredMsg: t("forms.required_field"),
    error: t("attendance.loadError"),
  };

  const tableColumns = [
    { key: "date", label: t(`${trans_prefix}.staff_individual_table_cols.date`) },
    { key: "check_in", label: t(`${trans_prefix}.staff_individual_table_cols.check_in`) },
    { key: "check_out", label: t(`${trans_prefix}.staff_individual_table_cols.check_out`) },
    { key: "total_hours", label: t(`${trans_prefix}.staff_individual_table_cols.total_hours`) },
  ];

  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staff, setStaff] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [tableData, setTableData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);

  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    const fetchStaff = async () => {
      setLoadingStaff(true);
      try {
        const url = API_URLS.getStaff(auth.id, school.id);
        const response = await axiosPrivate.get(url);
        const staff = response.data;
        let staffData = [];
        if (staff) {
          if (Array.isArray(staff)) {
            staffData = staff;
          }
        }

        const formattedStaff = staffData.map((staff) => ({
          value: staff.id,
          label: `${staff.FirstName || ''} ${staff.LastName || ''}`.trim()
        }));

        setStaff(formattedStaff);
      } catch (err) {
        console.error('Error fetching staff:', err);
        console.error('Error details:', err.response?.data);
        toast.error(trans.error);
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchStaff();
  }, [axiosPrivate, trans.error, school.id, auth.id]);

  const handleFilter = async () => {
    // Validate inputs
    const newErrors = {
      staff: !selectedStaff?.value,
      month: !month,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) {
      console.log('Validation failed:', newErrors);
      return;
    }

    setLoadingResult(true);
    try {
      const [year, monthNum] = month.split("-");
      if (!selectedStaff?.value) {
        throw new Error('No staff ID available');
      }
      if (!school?.id) {
        throw new Error('No nursery ID available');
      }
      const url = API_URLS.getStaffIndividualAttendance(selectedStaff.value, monthNum, school.id);
      const response = await axiosPrivate.get(url);

      // Transform the API response (array of records)
      const transformedData = response.data.map(record => ({
        date: record.date,
        check_in: record.checkin || '-',
        check_out: record.checkout || '-',
        total_hours: record.total_hours != null ? Number(record.total_hours).toFixed(2) : '-'
      }));

      setTableData(transformedData);
    } catch (err) {
      toast.error(trans.error);
    } finally {
      setLoadingResult(false);
    }
  };

  return (
    <div>
      <div className="d-flex gap-3 mb-4">
        <GeneralDropdown
          placeholder={trans.selectStaff}
          options={staff}
          selectedOption={selectedStaff}
          onChange={setSelectedStaff}
          errMsg={errors.staff && trans.requiredMsg}
          disabled={loadingStaff}
        />
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="form-control w-25"
        />
        <Button onClick={handleFilter}>{t("buttons.filter")}</Button>
      </div>

      {loadingResult ? (
        <LoadingDots />
      ) : (
        <div ref={reportRef}>
          <h4>{trans.title}</h4>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length}>{trans.noRecords}</TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, index) => (
                    <TableRow key={index}>
                      {tableColumns.map((col) => (
                        <TableCell key={col.key}>{row[col.key] || "-"}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );
};
